import { CancellationError, reportException, SkywardException } from "#skyward_error";
import { formatServerDate } from "./skyward.base.time";
import { getRowDataHash, getValue } from "./skyward.ui.controls";
import { getSearchParameters as dmseGetSearchParameters } from "./skyward.ui.distinctMultiSelectEEL.values";
import type { DependentsData, DependentRule } from "./skyward.ui.dependents";

// Constant to indicate that a control is waiting for server data
export const BUSY_HASH_PREFIX = "__BUSY__";
// Constant to indicate that a control is waiting to be initialized
export const INITIALIZING_HASH = "__INITIALIZING__";
// If we are still waiting for dependencies after this many attempts, give up
const MAX_DEPENDENCY_WAIT_ROUNDS = 25;

export interface RequestDataHash
{
    hash: string;
    // for eels
    initialValue?: string;
    initialValueHash?: string;
    // for dmses
    initialValues?: Record<string, string>;
    dependencies: Record<string, string | null>;
};

export type TextBoxData =
{
    dynamicfilterfield: string;
    dynamicfiltervalue: string;
    dynamicfilterattribute?: string | undefined;
};


export interface DependencyItemData
{
    ControlID: string;
    PostKey: string;
}

export interface DependencyData
{
    dependencyItems: DependencyItemData[];
}

const requestDataCache = Symbol("requestDataCache");
type RequestDataHashElement = HTMLElement &
{
    [requestDataCache]: Record<string, RequestDataHash>
};

function inIframeCheck()
{
    try
    {
        return window.self !== window.parent;
    }
    catch (e)
    {
        // If cross-origin, accessing window.parent will throw
        return false;
    }
}

export function getRequestDataHashes(requestDataHashSrc: string)
{
    let documentToUse = document;
    const isInIframe = inIframeCheck();
    if (isInIframe)
    {
        documentToUse = window.parent.document
    }

    let requestDataJsonElement = documentToUse.getElementById(requestDataHashSrc) as RequestDataHashElement | null;

    if (isInIframe && requestDataJsonElement == null)
    {
        requestDataJsonElement = document.getElementById(requestDataHashSrc) as RequestDataHashElement | null;
    }

    if (requestDataJsonElement == null)
    {
        console.warn(`Unable to find requestDataHash script with ID ${requestDataHashSrc}`);
        return;
    }
    return (requestDataJsonElement[requestDataCache] ??= JSON.parse(requestDataJsonElement.textContent!));
}

export function getRequestDataHash(requestDataHashSrc: string, id: string)
{
    const hashes = getRequestDataHashes(requestDataHashSrc);
    if (!hashes)
    {
        return;
    }
    const hash = hashes[id];
    if (!hash)
    {
        console.warn(`Unable to find requestDataHash for control with ID ${id}`);
    }
    return hash;
}

declare const dependencySnapshotBrand: unique symbol;

/**
 * Dependency values and hashes, read in the same synchronous step as confirming none of the dependencies were busy.
 * Get one from {@link waitForDependencies}. Dependencies can go busy again after that step, so build requests from
 * the snapshot instead of reading the dependencies again.
 */
export type DependencySnapshot = Record<string, string | string[]> & { readonly [dependencySnapshotBrand]: true };

/**
 * Reads dependency values without waiting for busy dependencies, which sends values without their hashes.
 * Only for paths that don't wait yet; requests should use {@link waitForDependencies}.
 */
export function getDependencyDataWithoutWaiting(textBoxData: TextBoxData | undefined, container: JQuery<HTMLElement>)
{
    return readDependencyData(textBoxData, container);
}

function readDependencyData(textBoxData: TextBoxData | undefined, container: JQuery<HTMLElement>)
{
    const postData = {} as DependencySnapshot;
    if (!textBoxData?.dynamicfiltervalue)
    {
        return postData;
    }
    const dependentFields = textBoxData.dynamicfilterfield.split(",");
    const dependentValues = textBoxData.dynamicfiltervalue.split(",");
    const dependentAttributes = textBoxData.dynamicfilterattribute?.split(",");
    container ??= $(document.documentElement);

    for (let i = 0; i < dependentFields.length; i++)
    {
        if (dependentValues[i].length === 0)
        {
            continue;
        }
        const field = dependentFields[i];
        const dependentElement = findDependentElement(dependentValues[i], container);

        const dependentStringArray = dependentAttributes?.[i].split("-");
        if (dependentStringArray && dependentStringArray.length >= 3 && dependentStringArray[2].length > 0)
        {
            const finalDependentString = dependentStringArray[1] + dependentStringArray[2].charAt(0).toUpperCase() + dependentStringArray[2].slice(1);

            if (typeof dependentElement.data(finalDependentString) !== "undefined")
            {
                postData[field] = dependentElement.data(finalDependentString);
            }
        }
        else
        {
            if (dependentElement.hasClass("dmsBackingData"))
            {
                postData[field] = dmseGetSearchParameters(dependentElement as JQuery<HTMLInputElement>);
            }
            else
            {
                let value = getValue(dependentElement);
                if (value instanceof Date)
                {
                    // TODO (6145639) Update this to account for new datepickers (using the capital P .datePicker class)
                    value = value.isValidDate()
                        ? formatServerDate(value, !dependentElement.hasClass("datepicker"))
                        : null;
                }
                postData[field] = value;
            }
            const hash = getRowDataHash(dependentElement);
            const valueForHash = postData[field];
            const hasValueForHash = isNotEmpty(valueForHash);
            if (hasValueForHash && hash !== null && hash !== undefined && !isBusyOrInitializing(hash))
            {
                postData[`${field}_hash`] = hash;
            }
        }
    }

    return postData;
}

export function isNotEmpty(value: unknown)
{
    return value !== null && value !== undefined && value !== "";
}

/**
 * Waits until none of the dependencies are busy, then reads their values and hashes in the same synchronous step.
 * Throws a CancellationError if the signal is aborted or the dependencies never finish loading.
 */
export async function waitForDependencies(textBoxData: TextBoxData | undefined, container: JQuery<HTMLElement>, signal?: AbortSignal): Promise<DependencySnapshot>
{
    container ??= $(document.documentElement);
    // Initializing controls that never trigger controlLoaded; waiting for them again would only repeat the timeout.
    const timedOutElements = new Set<HTMLElement>();
    let loadingElements = getLoadingDependencies(textBoxData, container, timedOutElements);

    for (let round = 0; round < MAX_DEPENDENCY_WAIT_ROUNDS; round++)
    {
        // Always yields at least once. That doesn't guarantee writers like ajax responses have finished; checking for
        // busy dependencies and reading them in the same synchronous step is what keeps values and hashes consistent.
        await waitForLoadingDependencies(loadingElements, timedOutElements, signal);
        if (signal?.aborted)
        {
            throw new CancellationError();
        }

        loadingElements = getLoadingDependencies(textBoxData, container, timedOutElements);
        if (loadingElements.length === 0)
        {
            return readDependencyData(textBoxData, container);
        }
    }

    // We can't complete the dependency, possibly because of a refresh loop
    throw new SkywardException("Dependencies are stuck in a loop", JSON.stringify(
    {
        loadingElements: loadingElements.map(element => element.attr("name") ?? element.attr("id")),
    }));
}

function getLoadingDependencies(textBoxData: TextBoxData | undefined, container: JQuery<HTMLElement>, timedOutElements: Set<HTMLElement>)
{
    const loadingElements: JQuery<HTMLElement>[] = [];
    if (!textBoxData?.dynamicfiltervalue)
    {
        return loadingElements;
    }
    const dependentValues = textBoxData.dynamicfiltervalue.split(",");

    for (const dependentValue of dependentValues)
    {
        if (dependentValue.length === 0)
        {
            continue;
        }
        const dependentElement = findDependentElement(dependentValue, container);
        const hash = getRowDataHash(dependentElement);

        // Controls with null hashes are not actively loading and may be replaced during workflow template changes.
        if (dependentElement.length === 0 ||
            !isBusyOrInitializing(hash) ||
            (hash === INITIALIZING_HASH && timedOutElements.has(dependentElement[0])))
        {
            continue;
        }
        loadingElements.push(dependentElement);
    }
    return loadingElements;
}

async function waitForLoadingDependencies(loadingElements: JQuery<HTMLElement>[], timedOutElements: Set<HTMLElement>, signal?: AbortSignal)
{
    const promises: Promise<void>[] = [];

    for (const dependentElement of loadingElements)
    {
        // If the hash is BUSY, we need to wait indefinitely for it to resolve.
        // Otherwise use a timeout for controls that forgot to trigger controlLoaded.
        const isBusy = isBusyHash(getRowDataHash(dependentElement));

        const completed = new Promise<void>(res => 
        {
            if (isBusy)
            {
                dependentElement.one("controlLoaded", () => res());
            }
            else
            {
                dependentElement.one("controlLoaded", () =>
                {
                    const finalHash = getRowDataHash(dependentElement);
                    if (finalHash === null)
                    {
                        console.warn("filterable control hash is still null after loading", dependentElement);
                    }
                    res();
                });
            }
        });

        if (isBusy)
        {
            // No timeout when waiting for busy controls
            promises.push(completed);
        }
        else
        {
            // It should take at most until the next microtask checkpoint for all the controls to finish loading. If they
            // haven't notified us after a full second, probably they forgot to trigger controlLoaded. When that happens,
            // we log a warning and keep going.
            const timeout = new Promise<void>((_res, rej) => { setTimeout(rej, 1000); });
            promises.push(Promise.race([completed, timeout]).catch(() =>
            {
                console.warn("filterable control hash timed out", dependentElement);
                timedOutElements.add(dependentElement[0]);
            }));
        }
    }
    if (signal?.aborted)
    {
        return;
    }
    const { promise: abortPromise, resolve: abortResolve } = Promise.withResolvers<void>();
    const abortHandler = () => abortResolve();
    signal?.addEventListener("abort", abortHandler);
    return Promise.race([
        Promise.all(promises),
        abortPromise,
    ])
    .finally(() => signal?.removeEventListener("abort", abortHandler));
}

/** Add this control to the dependents list of all its dependencies, so that they know to refresh or revalidate it on change. */
export function setupDependency(textBoxData: TextBoxData | undefined, dependentData: DependentRule, container: JQuery<HTMLElement>)
{
    if (!textBoxData?.dynamicfiltervalue)
    {
        return;
    }

    const dependentFields = textBoxData.dynamicfilterfield.split(",");
    const dependentValues = textBoxData.dynamicfiltervalue.split(",");

    for (let i = 0; i < dependentFields.length; i++)
    {
        if (dependentValues[i].length === 0)
        {
            continue;
        }
        let dependentElement = findDependentElement(dependentValues[i], container);
        if (dependentElement.length === 0)
        {
            // Browses do this. TODO check if it's needed
            dependentElement = container.findByIdWithUnknownPrefix(dependentValues[i]);
        }
        if (dependentElement.length === 0)
        {
            continue;
        }
        const itemDependents: DependentsData = dependentElement.data("dependents") ?? { dependents: [] };

const existingDependent = itemDependents.dependents.find((dependent) => dependent.Id === dependentData.Id);
        if (existingDependent)
        {
            Object.assign(existingDependent, dependentData);
        }
        else
        {
            itemDependents.dependents.push(dependentData);
        }

        dependentElement.data("dependents", itemDependents);
    }
}

export type TransactionInstance =
{
    rollback(): void;
    setValue(value: string): void;
}

export class HashTransaction
{
    element: JQuery<HTMLInputElement>;
    data: { selectedValueHash: string };
    counter: number;
    constructor(element: JQuery<HTMLInputElement>)
    {
        this.element = element;
        this.data = element.data() as any;
        this.counter = 0;
    }

    run<T>(signal: AbortSignal, fn: (transaction: TransactionInstance) => JQuery.Promise<T> | JQuery.jqXHR): Promise<T>;
    run<T>(signal: AbortSignal, fn: (transaction: TransactionInstance) => Promise<T>): Promise<T>;
    run<T>(signal: AbortSignal, fn: (transaction: TransactionInstance) => T): Promise<Awaited<T>>;
    async run<T>(signal: AbortSignal, fn: (transaction: TransactionInstance) => Awaited<T>): Promise<T | null>
    {
        const oldHash = this.data.selectedValueHash;
        if (isBusyHash(oldHash))
        {
            throw new Error("Cannot nest transactions");
        }
        const oldValue = this.element.val()!;
        const busyHash = `${BUSY_HASH_PREFIX}${this.counter++}`;
        this.data.selectedValueHash = busyHash;
        let assignedValue: string | undefined;
        const transaction: TransactionInstance =
        {
            setValue: (value: string) =>
            {
                this.element.val(value);
                assignedValue = this.element.val(); // handle coercion to string
            },
            rollback: () =>
            {
                // If the hash didn't get set before exiting, clear out our busy flag
                if (this.data.selectedValueHash === busyHash)
                {
                    this.data.selectedValueHash = oldHash;
                    // If the caller assigned the value but not the hash, reset it to agree with the old hash
                    if (this.element.val() === assignedValue)
                    {
                        this.element.val(oldValue);
                    }
                }
            },
        };

        try
        {
            signal.addEventListener("abort", transaction.rollback);
            const result = await fn(transaction);
            signal.removeEventListener("abort", transaction.rollback);
            return result;
        }
        finally
        {
            // Clean up if we exited early.
            // This only does anything if (a) the caller did not commit a change (no writes to selectedValueHash)
            // (b) we didn't abort (which would have called rollback())
            // (c) another transaction started. Specifically, the following can happen:
            //   1. The transaction is aborted. rollback() is called synchronously from the event handler, resetting the hash.
            //   2. Another transaction starts, and passes isBusyHash() because the hash is already reset
            //   3. We complete the `await` and reach this block
            transaction.rollback();

            // Ensure that waiters are notified and stop waiting, even if we bailed out.
            if (!isBusyHash(this.data.selectedValueHash))
            {
                const reason = signal.aborted ? "aborted" : "done";
                this.element.trigger("controlLoaded", reason);
            }
        }
    }
}

export function isBusyHash(hash: string | null | undefined)
{
    return hash?.startsWith(BUSY_HASH_PREFIX) ?? false;
}

export function isBusyOrInitializing(hash: string | null | undefined)
{
    return isBusyHash(hash) || hash === INITIALIZING_HASH;
}

function findDependentElement(rawName: string, container: JQuery<HTMLElement>): JQuery<HTMLElement>
{
    const name = CSS.escape(rawName);
    const valueElementSelector = ".dmsBackingData,.tileViewEEL_value,.accountDimensionEELValue,.codeEELValue,.htmlComboBox_value";
    let dependentElement = container.find(`[name="${name}"]`);
    if (dependentElement.length > 0)
    {
        return dependentElement;
    }
    dependentElement = container.find(`#${name}`).find(`${valueElementSelector}`).first();
    if (dependentElement.length > 0)
    {
        return dependentElement;
    }
    if (container[0]?.tagName !== "HTML")
    {
        return findDependentElement(rawName, $(document.documentElement));
    }
    return $();
}
